import { Alert, MoneyValue, formikHelpers, toast, lucideIcons } from '@round/creatorbase-ui';
import { Form, useFormik, useFormikContext } from 'formik';
import { ProjectCampaignOption } from '../useProjectCampaignGroups';
import {
    useCreatePost,
    useCreatorbaseCurrencies,
    useInvalidateAggregatedPostStats,
    useInvalidateCampaigns,
    useInvalidatePosts,
    useInvalidateProject,
    usePreviewPostCost,
} from '@round/creatorbase-helpers/api-hooks';
import { creatorbase } from '@round/api';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { ExplorePlatform } from '../../CreatorExploreTable/helpers';
import { asMoneyWithoutZeros } from 'helpers';
import styles from './CreatePostForm.module.css';
import PostCostFeesBreakdown from './PostCostFeesBreakdown';
import { getDefaultCost, isCurrencyMismatch, Rate } from './helpers';

const { MoneyInputField, Field } = formikHelpers;
const { AlertTriangle } = lucideIcons;

type FormValues = {
    cost: MoneyValue | null;
};

type Props = {
    campaign: ProjectCampaignOption | null;
    clientInfluencer: creatorbase.ClientInfluencer | null | undefined;
    /** Overrides the creator's saved rate — used where a rate belongs to the flow, not the creator's account. */
    rate?: Rate | null;
};

const CreatePostForm = ({ campaign, clientInfluencer, rate }: Props) => {
    const formik = useFormikContext<FormValues>();
    const { currencyOptions, getCurrencyById } = useCreatorbaseCurrencies();
    const { selectedClient } = useClientSelect();
    const areFeesEnabled = !!selectedClient?.is_payment_enabled;
    const isCostEditable = !!campaign?.is_post_cost_editable;

    const effectiveRate = rate ?? getAccountCost(campaign?.platform as ExplorePlatform, clientInfluencer);
    const currencyDiscrepancy = isCurrencyMismatch(effectiveRate, {
        isCostEditable,
        areFeesEnabled,
        campaignCurrencyId: campaign?.currency_id,
    });

    const {
        data: costPreview,
        isLoading: isPreviewLoading,
        error: costPreviewError,
    } = usePreviewPostCost(
        {
            campaign_id: campaign?.id,
            base_cost: formik.values.cost?.value ? Number(formik.values.cost.value) : undefined,
            currency_id: formik.values.cost?.currency?.value,
        },
        { enabled: areFeesEnabled && isCostEditable }
    );

    if (!campaign?.is_post_cost_editable) {
        return null;
    }

    return (
        <Form className={styles.form}>
            <Field name="cost">
                <Field.Label htmlFor="cost">Post Cost</Field.Label>
                <MoneyInputField name="cost" currencies={currencyOptions} isCurrencyDisabled={!areFeesEnabled} />
            </Field>

            {areFeesEnabled && (
                <>
                    <PostCostFeesBreakdown
                        campaignCurrency={getCurrencyById(campaign?.currency_id)}
                        totalCost={costPreview?.cost}
                        isTotalLoading={isPreviewLoading}
                        hasAmountError={!!costPreviewError}
                    />

                    {costPreviewError && (
                        <Alert variant="destructive">
                            <AlertTriangle />
                            <Alert.Description>Something went wrong fetching estimated fees</Alert.Description>
                        </Alert>
                    )}
                </>
            )}

            {!areFeesEnabled && currencyDiscrepancy && (
                <Alert>
                    <AlertTriangle />
                    <Alert.Description>
                        Campaign currency mismatch. Creator cost:&nbsp;
                        {asMoneyWithoutZeros(effectiveRate?.cost, getCurrencyById(effectiveRate?.currencyId))}
                    </Alert.Description>
                </Alert>
            )}
        </Form>
    );
};

type Params = {
    accountId: number | undefined;
    resolveAccountId?: () => Promise<number | undefined>;
    campaign: ProjectCampaignOption | null;
    clientInfluencer: creatorbase.ClientInfluencer | null | undefined;
    /** Overrides the creator's saved rate — used where a rate belongs to the flow, not the creator's account. */
    rate?: Rate | null;
};

type CreatePostOptions = {
    onSuccess?: (data: creatorbase.Post) => void;
};

export function useCreatePostForm(
    { accountId, resolveAccountId, campaign, clientInfluencer, rate }: Params,
    { onSuccess }: CreatePostOptions
) {
    const { getCurrencyOptionsById } = useCreatorbaseCurrencies();
    const { createAsync: createPost } = useCreatePost();
    const invalidatePosts = useInvalidatePosts();
    const invalidateAggregatedPostStats = useInvalidateAggregatedPostStats();
    const invalidateCampaigns = useInvalidateCampaigns();
    const invalidateProject = useInvalidateProject();
    const { selectedClient } = useClientSelect();
    const areFeesEnabled = !!selectedClient?.is_payment_enabled;
    const isPostCostEditable = !!campaign?.is_post_cost_editable;
    const platform = campaign?.platform;

    const effectiveRate = rate ?? getAccountCost(platform as ExplorePlatform, clientInfluencer);

    return useFormik<FormValues>({
        initialValues: {
            cost: isPostCostEditable
                ? getDefaultCost(effectiveRate, {
                      areFeesEnabled,
                      campaignCurrencyId: campaign?.currency_id,
                      getCurrencyOptionsById,
                  })
                : null,
        },
        enableReinitialize: true,
        onSubmit: async (values, helpers) => {
            if (!campaign) {
                return;
            }

            helpers.setSubmitting(true);

            // A creator picked somewhere they aren't saved yet has no account until the save makes
            // one, so the id is asked for here rather than required up front. A refusal is reported
            // by whoever does the saving, which is why nothing is said about it here.
            const clientInfluencerAccountId = accountId ?? (await resolveAccountId?.().catch(() => undefined));
            if (!clientInfluencerAccountId) {
                helpers.setSubmitting(false);
                return;
            }

            const payload: creatorbase.PostPostData = {
                campaign_id: campaign.id,
                client_influencer_account_id: clientInfluencerAccountId,
            };

            if (areFeesEnabled) {
                payload.base_cost = values.cost?.value ? Number(values.cost.value) : undefined;
                payload.currency_id = values.cost?.currency?.value;
            } else {
                payload.cost = values.cost?.value ? Number(values.cost.value) : undefined;
            }

            // Awaited rather than left to its own callbacks: the submission is what the form reports
            // as in flight, and a post fired off outside it would leave the picker free to be saved
            // again while the first one is still on the wire.
            try {
                const data = await createPost(payload);

                // Partial-match every cached posts list for this campaign (any ordering/
                // page size), so the post surfaces wherever it's read — regardless of which
                // header rendered the dropdown. Aggregated stats refresh too so campaign
                // totals reflect the new post.
                invalidatePosts({ campaign_id: data.campaign_id.toString() });
                invalidateAggregatedPostStats({ campaign_id: data.campaign_id });
                // invalidate platform headline stats on project details
                invalidateAggregatedPostStats({ project_id: campaign.project_id, platform: campaign.platform });
                invalidateCampaigns(data.campaign_id);
                invalidateProject(campaign.project_id);
                onSuccess?.(data);
                toast.success('Creator added to campaign');
                helpers.resetForm();
            } catch {
                toast.error('Could not add creator to campaign');
            } finally {
                helpers.setSubmitting(false);
            }
        },
    });
}

function getAccountCost(
    platform: ExplorePlatform,
    clientInfluencer: creatorbase.ClientInfluencer | null | undefined
): Rate | null {
    if (!platform || !clientInfluencer) {
        return null;
    }

    return {
        cost: clientInfluencer[`${platform}_cost`],
        currencyId: clientInfluencer[`${platform}_currency_id`],
    };
}

export default CreatePostForm;
